import { type ReactNode, createContext, useMemo } from 'react'

import {
  type SanitySiteFragment,
  type SanityStrings,
} from '@data/sanity/queries/types/site'

type StringsContextProps = SanityStrings

const emptySiteStrings: StringsContextProps = {
  loadingPageTitle: '',
  languageSwitch: '',
  cartLink: '',
  skipToContent: '',
  goHomeLabel: '',
  mobileMenuLabel: '',
  searchLabel: '',
  shopTitle: '',
  searchResultProducts: '',
  searchResultProductCount: '',
  searchResultProductsNotFound: '',
  searchResultShowAllProducts: '',
  searchingProducts: '',
  collectionProducts: '',
  collectionProductCount: '',
  collectionSortLabel: '',
  collectionSortDescription: '',
  collectionShowFiltersLabel: '',
  collectionQuickFilterAllLabel: '',
  collectionFilters: '',
  collectionRemoveFilter: '',
  collectionClearAllLabel: '',
  collectionClearFiltersLabel: '',
  collectionFilterResultsLabel: '',
  collectionFilterNoResultsLabel: '',
  collectionFilterModalClose: '',
  collectionFilterNoResults: [],
  productLowStock: '',
  productOutOfStock: '',
  productPreorderLabel: '',
  productPriceLabel: '',
  productColorOptionLabel: '',
  productDiscountText: '',
  productIncreaseQuantity: '',
  productDecreaseQuantity: '',
  productEnterQuantity: '',
  productPreorderCountdownFormat: '',
  productAvailabilityCountdownFormat: '',
  productAvailabilityCountdownCardFormat: '',
  productRelatedProducts: '',
  cartTitle: '',
  cartClose: '',
  cartEmpty: '',
  cartVatSectionTitle: '',
  cartVatIdInputPlaceholder: '',
  cartVatIdError: '',
  cartInvalidVatIdError: '',
  cartCommentSectionTitle: '',
  cartCommentInputPlaceholder: '',
  cartDiscount: '',
  cartSubtotal: '',
  cartTotal: '',
  cartSubmit: '',
  cartAddToCartErrorMessage: [],
  cartPreorderAttributeKey: '',
  cartPreorderAttributeValue: '',
  carouselCounterText: '',
  carouselLeftArrowLabel: '',
  carouselRightArrowLabel: '',
  carouselDotLabel: '',
  buttonAddToCart: '',
  buttonAddToCartPreorder: '',
  buttonRemove: '',
  buttonAccept: '',
  buttonLearnMore: '',
  buttonTryAgain: '',
  buttonLoadMore: '',
  buttonUnavailable: '',
  buttonWaiting: '',
  buttonLoading: '',
  buttonAdding: '',
  buttonUpdating: '',
  buttonSubmitting: '',
  buttonEdit: '',
  buttonDelete: '',
  buttonCancel: '',
  buttonShopNow: '',
  buttonBackTo: '',
  emailAddress: '',
  emailAddressPlaceholder: '',
  emailMissing: '',
  emailInvalid: '',
  firstName: '',
  firstNamePlaceholder: '',
  firstNameMissing: '',
  lastName: '',
  lastNamePlaceholder: '',
  lastNameMissing: '',
  fullName: '',
  fullNamePlaceholder: '',
  fullNameMissing: '',
  phoneNumber: '',
  phoneNumberPlaceholder: '',
  phoneNumberMissing: '',
  phoneNumberInvalid: '',
  company: '',
  companyPlaceholder: '',
  message: '',
  messagePlaceholder: '',
  productWaitlistSubmit: '',
  productWaitlistSuccess: '',
  productWaitlistError: '',
  searchPlaceholder: '',
  blogTitle: '',
  blogRelatedArticlesTitle: '',
  blogPostReadMore: '',
  blogPostByAuthor: '',
  teamMemberCardLinkedIn: '',
  teamMemberCardBlogArticles: '',
  passwordProtectionSubmitButton: '',
  passwordProtectionIncorrectPasswordError: '',
  passwordProtectionError: '',
}

export const StringsContext =
  createContext<StringsContextProps>(emptySiteStrings)

interface StringsContextProviderProps {
  site: SanitySiteFragment
  children: ReactNode
}

export const StringsContextProvider = ({
  site,
  children,
}: StringsContextProviderProps) => {
  const strings = useMemo(
    () => ({
      ...emptySiteStrings,
      ...site.strings,
    }),
    [site.strings],
  )

  return (
    <StringsContext.Provider value={strings}>
      {children}
    </StringsContext.Provider>
  )
}
